import { Link } from 'react-router-dom';
import {
  Building2, Calendar, Shield, Users, FileText, CheckCircle2,
  XCircle, Clock, ExternalLink, Layers,
} from 'lucide-react';
import { POLICY_SECTIONS, getPolicyTypeMeta, getPolicyProgress, isFieldVisible } from '../../config/policyFieldConfig';
import { formatFieldValue } from '../../utils/formatters';

const SECTION_ICONS = {
  company: Building2,
  period: Calendar,
  coverage: Shield,
  claims: FileText,
  eligibility: Users,
  contacts: Users,
};

const STATUS_STYLES = {
  active: { bg: 'bg-emerald-500', text: 'Active', ring: 'ring-emerald-200' },
  renewal: { bg: 'bg-amber-500', text: 'Renewal Due', ring: 'ring-amber-200' },
  expired: { bg: 'bg-red-500', text: 'Expired', ring: 'ring-red-200' },
};

export default function PolicyDetailView({ policy, canEdit, onEdit }) {
  const typeMeta = getPolicyTypeMeta(policy.policy_type);
  const progress = getPolicyProgress(policy.policy_start_date, policy.policy_end_date);
  const status = STATUS_STYLES[progress.status];

  return (
    <div className="space-y-6">
      {/* Hero header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary-700 via-primary-600 to-primary-800 text-white shadow-xl">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -right-20 -top-20 w-64 h-64 rounded-full bg-white" />
          <div className="absolute -left-10 bottom-0 w-48 h-48 rounded-full bg-white" />
        </div>
        <div className="relative p-6 sm:p-8">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${typeMeta.color}`}>
                  {typeMeta.label}
                </span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium bg-white/20 backdrop-blur`}>
                  {policy.policy_number || 'No policy #'}
                </span>
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ring-2 ${status.ring} bg-white/95 text-slate-800`}>
                  <span className={`w-2 h-2 rounded-full ${status.bg}`} />
                  {status.text}
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{policy.company_name}</h1>
              {policy.group_company_name && (
                <p className="text-primary-100 mt-1">{policy.group_company_name}</p>
              )}
              <p className="text-primary-200 mt-2 flex items-center gap-2 text-sm sm:text-base">
                <Shield className="w-4 h-4 shrink-0" />
                {policy.provider_name}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to={`/slabs?policy=${policy.id}`} className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-white/15 hover:bg-white/25 text-sm font-medium transition-colors">
                <Layers className="w-4 h-4" /> Age Slabs
              </Link>
              {canEdit && (
                <button onClick={onEdit} className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-white text-primary-700 hover:bg-primary-50 text-sm font-semibold shadow-lg transition-colors">
                  Edit Policy
                </button>
              )}
            </div>
          </div>

          {/* Key metrics strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8">
            <MetricCard label="Sum Insured" value={formatFieldValue({ type: 'currency' }, policy.sum_insured)} />
            <MetricCard label="Co-payment" value={formatFieldValue({ type: 'percent' }, policy.co_payment_percentage)} />
            <MetricCard label="Days Remaining" value={progress.daysLeft} highlight={progress.status === 'renewal'} />
            <MetricCard label="Claim TAT" value={policy.claim_settlement_tat ? `${policy.claim_settlement_tat} days` : '—'} />
          </div>

          {/* Policy period progress */}
          <div className="mt-6">
            <div className="flex justify-between text-xs text-primary-200 mb-2">
              <span>{formatFieldValue({ type: 'date' }, policy.policy_start_date)}</span>
              <span>{progress.percent}% elapsed</span>
              <span>{formatFieldValue({ type: 'date' }, policy.policy_end_date)}</span>
            </div>
            <div className="h-2 rounded-full bg-white/20 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${progress.status === 'expired' ? 'bg-red-400' : progress.status === 'renewal' ? 'bg-amber-400' : 'bg-emerald-400'}`}
                style={{ width: `${progress.percent}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Section cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {POLICY_SECTIONS.map((section) => {
          const Icon = SECTION_ICONS[section.id] || FileText;
          const fields = section.fields.filter((f) => isFieldVisible(f, policy));
          if (fields.length === 0) return null;

          return (
            <div key={section.id} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100 bg-slate-50/80">
                <div className="p-2 rounded-lg bg-primary-50 text-primary-600">
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="font-semibold text-slate-900">{section.title}</h2>
                  {section.description && <p className="text-xs text-slate-500">{section.description}</p>}
                </div>
              </div>
              <dl className="p-6 space-y-4">
                {fields.map((field) => {
                  const value = formatFieldValue(field, policy[field.key], policy);
                  const isLong = field.type === 'textarea' || (typeof value === 'string' && value.length > 60);

                  return (
                    <div key={field.key} className={isLong ? 'col-span-full' : ''}>
                      <dt className="text-xs font-medium text-slate-400 uppercase tracking-wide">{field.label}</dt>
                      <dd className="mt-1 text-sm text-slate-800 font-medium">
                        {field.type === 'boolean' ? (
                          <BoolBadge value={policy[field.key]} />
                        ) : field.key === 'cashless_hospitals' && policy.cashless_hospitals?.startsWith('http') ? (
                          <a href={policy.cashless_hospitals} target="_blank" rel="noreferrer" className="text-primary-600 hover:underline inline-flex items-center gap-1">
                            Network hospitals <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        ) : (
                          <span className={isLong ? 'block text-slate-600 font-normal leading-relaxed' : ''}>{value}</span>
                        )}
                      </dd>
                    </div>
                  );
                })}
              </dl>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MetricCard({ label, value, highlight }) {
  return (
    <div className={`rounded-xl px-4 py-3 ${highlight ? 'bg-amber-400/20 ring-1 ring-amber-300/50' : 'bg-white/10'}`}>
      <p className="text-xs text-primary-200">{label}</p>
      <p className="text-lg sm:text-xl font-bold mt-0.5">{value}</p>
    </div>
  );
}

function BoolBadge({ value }) {
  return value ? (
    <span className="inline-flex items-center gap-1 text-emerald-600"><CheckCircle2 className="w-4 h-4" /> Yes</span>
  ) : (
    <span className="inline-flex items-center gap-1 text-slate-400"><XCircle className="w-4 h-4" /> No</span>
  );
}
